import React, { useRef, useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { countryService } from "../../utils/services/country.service";
import { provinceService } from "../../utils/services/province.service";
import TextField from "@mui/material/TextField";
import CustomTypography from "../../globalStyles/fonts";
import { CircularProgress } from "@mui/material";
import { fieldSx } from "../../pages/manage-catalog/constants";

const AddressLocationFields = ({
  address,
  errors,
  onProvinceChange,
  onCountryChange,
  onZipChange,
  onCityChange,
  type = "shipping",
  onProvinceSelect,
  onDataChange,
  optionalFields = [],
}) => {
  const selectedCountry = useRef();
  const [isLoadingZip, setIsLoadingZip] = useState(false);
  const [zipError, setZipError] = useState("");
  const places = useRef();
  const debounceTimeout = useRef();
  const [manualEntry, setManualEntry] = useState(false);

  const fetchLocationByZip = async (countryCode, zipCode) => {
    if (!countryCode || !zipCode || zipCode.trim().length < 3) return;

    setIsLoadingZip(true);
    setZipError("");

    try {
      const response = await fetch(
        `https://api.zippopotam.us/${countryCode}/${zipCode.trim()}`,
      );

      if (!response.ok)
        throw new Error("Invalid zip code or country combination");

      const data = await response.json();

      if (data?.places?.length > 0) {
        places.current = data.places;
        setManualEntry(false);

        const stateAbbr = data.places[0]["state abbreviation"];
        const placeName = data.places[0]["place name"];
        onCityChange(placeName);

        if (selectedCountry.current?._id) {
          const query = "$limit=15&$skip=0";
          const provinceResponse = await provinceService.fetch(
            "",
            query,
            "code",
            selectedCountry.current._id,
            [stateAbbr],
          );

          if (provinceResponse?.total === 1) {
            onProvinceChange(provinceResponse.data[0]);
          }
        }
      } else {
        setZipError("No state/province found for this zip code");
        setManualEntry(true);
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
      setZipError("Invalid zip code or unable to fetch location data");
      onProvinceChange(null);
      onCityChange("");
      places.current = null;
      setManualEntry(true);
    } finally {
      setIsLoadingZip(false);
    }
  };

  const handleZipChange = (value) => {
    onZipChange(value);
    setZipError("");
    if (selectedCountry.current?.code2 != "US") {
      setManualEntry(true);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (selectedCountry.current?.code2 && value.trim().length >= 3) {
        fetchLocationByZip(selectedCountry.current.code2.toLowerCase(), value);
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const onChangeCountry = (country) => {
    onZipChange("");
    onCityChange("");
    places.current = null;
    selectedCountry.current = country;
    onCountryChange(country);
    setZipError("");
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  };

  const onChangeProvince = (province) => {
    setZipError("");
    if (!manualEntry) {
      onCityChange("");
      onZipChange("");
      places.current = null;
    }
    onProvinceSelect(province);
  };

  const handleCityChange = (value) => {
    if (!manualEntry) {
      onZipChange("");
    }
    onCityChange(value);
  };

  return (
    <>
      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          Country Code{" "}
          {optionalFields.includes("country_code") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.country_code
              ? { code2: `${address?.country_code} (${address?.country})` }
              : { code2: "" }
          }
          customLabel="Country Code"
          onDataChange={onChangeCountry}
          width="100%"
          optionKey={(country) =>
            `${country.code2} ${country.name ? `(${country.name})` : ""}`
          }
          apiEndPoint={(limit, skip, search = "") => {
            const query = `$limit=${limit}&$skip=${skip}`;
            return countryService.fetch(search, query, "code2");
          }}
          errorMessage={errors[type ? `${type}_country_code` : "country_code"]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          Country{" "}
          {optionalFields.includes("country") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.country ? { name: address?.country } : { name: "" }
          }
          customLabel="Country"
          onDataChange={onChangeCountry}
          width="100%"
          optionKey="name"
          apiEndPoint={(limit, skip, search = "") => {
            const query = `$limit=${limit}&$skip=${skip}`;
            return countryService.fetch(search, query, "name");
          }}
          errorMessage={errors[type ? `${type}_country` : "country"]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400, marginBottom: 1 }}
          fontSize={14}
        >
          Zip{" "}
          {optionalFields.includes("zip") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
          {isLoadingZip && (
            <CircularProgress size={12} sx={{ ml: 1, color: "#1976d2" }} />
          )}
        </CustomTypography>
        <TextField
          sx={fieldSx}
          size="small"
          required
          fullWidth
          value={address?.zip || ""}
          onChange={(e) => handleZipChange(e.target.value)}
          error={!!errors[type ? `${type}_zip` : "zip"]}
          helperText={
            errors[type ? `${type}_zip` : "zip"] ||
            zipError ||
            (selectedCountry.current?.code2 === "US"
              ? "Enter zip code to auto-populate location details (~2 second delay)"
              : "")
          }
          disabled={!(selectedCountry.current?.code2 || address?.country_code)}
          placeholder={
            selectedCountry.current?.code2
              ? "Enter zip code..."
              : "Select country first"
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          State/Province Code
          {optionalFields.includes("province_code") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.province_code
              ? { code: `${address?.province_code} (${address?.province})` }
              : { code: "" }
          }
          customLabel="Province Code"
          onDataChange={onChangeProvince}
          width="100%"
          optionKey={(province) =>
            `${province.code} ${province.name ? `(${province.name})` : ""}`
          }
          apiEndPoint={(limit, skip, search = "") => {
            const query = `$limit=${limit}&$skip=${skip}`;
            return provinceService.fetch(
              search,
              query,
              "code",
              selectedCountry?.current?._id,
              places?.current?.map((place) => place["state abbreviation"]),
            );
          }}
          errorMessage={
            errors[type ? `${type}_province_code` : "province_code"]
          }
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          State/Province{" "}
          {optionalFields.includes("province") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
        </CustomTypography>
        <AutocompleteWithPagination
          hideLabel
          selectedItem={
            address?.province ? { name: address?.province } : { name: "" }
          }
          customLabel="Province"
          onDataChange={onChangeProvince}
          width="100%"
          optionKey="name"
          apiEndPoint={(limit, skip, search = "") => {
            const query = `$limit=${limit}&$skip=${skip}`;
            return provinceService.fetch(
              search,
              query,
              "name",
              selectedCountry?.current?._id,
            );
          }}
          errorMessage={errors[type ? `${type}_province` : "province"]}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <CustomTypography
          style={{ textAlign: "left", fontWeight: 400 }}
          fontSize={14}
        >
          City{" "}
          {optionalFields.includes("city") ? (
            <></>
          ) : (
            <span style={{ color: "red" }}>*</span>
          )}
        </CustomTypography>
        {manualEntry ? (
          <TextField
            sx={fieldSx}
            size="small"
            required
            fullWidth
            value={address?.city || ""}
            onChange={(e) => handleCityChange(e.target.value)}
            error={!!errors[type ? `${type}_city` : "city"]}
            helperText={errors[type ? `${type}_city` : "city"] || ""}
            placeholder="Enter city"
            margin="dense"
          />
        ) : (
          <AutocompleteWithPagination
            hideLabel
            selectedItem={
              address?.city
                ? { "place name": address?.city }
                : { "place name": "" }
            }
            customLabel="City"
            onDataChange={(city) =>
              handleCityChange(city ? city["place name"] : "")
            }
            width="100%"
            optionKey="place name"
            data={places.current?.length ? places.current : []}
            errorMessage={errors[type ? `${type}_city` : "city"]}
          />
        )}
      </Grid>
    </>
  );
};

export default AddressLocationFields;
